/**
 * TicketListItem — compact row in the inbox list.
 * Pure presentational; parent supplies all handlers.
 */
import React from 'react';
import { MapPin, Paperclip, MessageSquare, Clock, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  stripHtml,
  formatDateCompact,
  StatusBadge,
  PriorityBadge,
} from './TicketComponents';
import {
  internalStatusBadgeClasses,
  formatDueDate,
  dueDateRelative,
} from '../../lib/ticketFields';

export function TicketListItem({
  ticket,
  selected,
  onSelect,
  compact = false,
  showInlineStatus = false,
  onStatusChange,
  showAssignee = false,
  showInlineAssignee = false,
  assignees = [],
  onAssigneeChange,
  internalStatuses = [],
}) {
  const subject =
    (ticket.subject || '').trim() ||
    stripHtml(ticket.text).split('\n').filter(Boolean)[0] ||
    stripHtml(ticket.text);
  const preview = subject.slice(0, compact ? 70 : 90);
  const currentInternal = internalStatuses.find((i) => i.id === ticket.internal_status);
  const dueIso = ticket.due_date;
  const dueRel = dueIso ? dueDateRelative(dueIso) : '';
  const dueOverdue = dueIso && new Date(dueIso).getTime() < Date.now() && ticket.status !== 'resolved';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect?.();
      }}
      className={`w-full text-left p-3 border-b border-slate-100 transition-colors hover:bg-slate-50 cursor-pointer ${
        selected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
      }`}
      data-testid={`ticket-list-item-${ticket.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {ticket.customer_replied_unread && (
              <span
                className="relative inline-flex items-center"
                title="Customer replied — unread"
                data-testid="unread-dot"
              >
                <span className="absolute inline-flex h-2 w-2 rounded-full bg-rose-500 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
              </span>
            )}
            <Badge
              variant="outline"
              className="bg-slate-900 text-white border-slate-900 font-mono text-xs"
            >
              #{ticket.ticket_number}
            </Badge>
            {!compact && <PriorityBadge priority={ticket.priority} />}
          </div>

          <div className={`text-sm line-clamp-1 mb-1 text-slate-900 ${ticket.customer_replied_unread ? 'font-bold' : 'font-medium'}`}>
            {preview || '(no subject)'}
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {ticket.location_name}
            </span>
            <span>{formatDateCompact(ticket.created_at)}</span>
          </div>

          {showAssignee && (ticket.assigned_to_name || ticket.assigned_to_email) && (
            <div className="mt-1 text-xs text-slate-500">
              Assignee:{' '}
              <span className="text-slate-700">
                {ticket.assigned_to_name || ticket.assigned_to_email}
              </span>
            </div>
          )}

          {!compact && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className="bg-purple-50 text-purple-700 border-purple-200 text-xs"
              >
                {ticket.module}
              </Badge>
              {currentInternal && (
                <Badge
                  variant="outline"
                  className={`${internalStatusBadgeClasses(currentInternal.color)} text-[11px] gap-1`}
                  title={`Internal status: ${currentInternal.label}`}
                  data-testid="list-internal-status-badge"
                >
                  <Tag className="w-3 h-3" />
                  {currentInternal.label}
                </Badge>
              )}
              {dueIso && (
                <Badge
                  variant="outline"
                  className={`${dueOverdue
                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                    : 'bg-slate-50 text-slate-700 border-slate-200'} text-[11px] gap-1`}
                  title={`Estimated delivery: ${formatDueDate(dueIso)} (${dueRel})`}
                  data-testid="list-due-date-badge"
                >
                  <Clock className="w-3 h-3" />
                  {formatDueDate(dueIso)}
                </Badge>
              )}
              {ticket.attachments?.length > 0 && (
                <Badge
                  variant="outline"
                  className="bg-slate-50 text-slate-700 border-slate-200 text-xs"
                >
                  <Paperclip className="w-3 h-3 mr-1" />
                  {ticket.attachments.length}
                </Badge>
              )}
              {ticket.replies?.length > 0 && (
                <Badge
                  variant="outline"
                  className="bg-slate-50 text-slate-700 border-slate-200 text-xs"
                >
                  <MessageSquare className="w-3 h-3 mr-1" />
                  {ticket.replies.length}
                </Badge>
              )}
            </div>
          )}
        </div>

        <div
          className="flex flex-col items-end gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {showInlineStatus ? (
            <Select
              value={ticket.status}
              onValueChange={(v) => onStatusChange?.(v)}
            >
              <SelectTrigger className="h-6 w-28 px-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                <SelectItem value="under_investigation">Under Investigation</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <StatusBadge status={ticket.status} />
          )}

          {showInlineAssignee && (
            <Select
              value={ticket.assigned_to_id || 'unassigned'}
              onValueChange={(v) =>
                onAssigneeChange?.(v === 'unassigned' ? '' : v)
              }
            >
              <SelectTrigger className="h-6 w-44 px-2">
                <SelectValue placeholder="Assignee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {assignees.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name || a.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="text-xs text-slate-400">
            {formatDateCompact(ticket.created_at)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketListItem;
